import { BreadcrumbService } from './../shared/services/breadcrumb.service';
import { RenegociacaoCartaoService } from 'src/app/renegociacao-cartao/renegociacao-cartao.service';
import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Colaborador } from 'src/app/shared/models/colaborador.model';
import { environment } from 'src/environments/environment';
import { ClientePreLogin } from '../shared/models/cliente-pre-login-model';
import { PreLoginResponse } from '../shared/models/pre-login-response-model';
import { TipoAutenticacao } from '../shared/models/tipo-autenticacao.enum';
import BrowserUtils from '../shared/utils/browser-utils';
import StringUtils from '../shared/utils/string-utils';
import { AutenticacaoAutomatica } from './../shared/models/autenticacao-automatica.model';
import { AutenticacaoCampanha } from './../shared/models/autenticacao-campanha.model';
import { AutenticacaoCliente } from './../shared/models/autenticacao-cliente.model';
import { AutenticacaoColaborador } from './../shared/models/autenticacao-colaborador.model';
import { Bandeira } from './../shared/models/bandeira.enum';
import { LogAcaoCliente } from './../shared/models/log-acao-cliente-enum';
import { LoginAutomatico } from './../shared/models/login-automatico.model';
import { RespostaAutenticacao } from './../shared/models/resposta-autenticacao.model';
import { TokenAutenticacao } from './../shared/models/token-autenticacao.model';
import { Usuario } from './../shared/models/usuario.model';
import { ApiService } from '../shared/api/api.service';

@Injectable({
  providedIn: 'root'
})
export class AutenticacaoService {

  private readonly KEY_BANDEIRA = 'bandeira';
  private readonly KEY_JWT_TOKEN_LOCAL_SOTAGE = 'token';
  private readonly KEY_CREDENCIAIS_COLABORADOR = 'colaborador';
  private readonly KEY_PRE_LOGIN_CLIENT_PHONE = 'client_phone';
  private readonly KEY_IS_AUTO_LOGIN = '@portal/ial';
  private readonly KEY_POSSUI_CARNE = '@portal/psc';
  private readonly KEY_PRE_LOGIN_ID = '@portal/plid';
  private readonly TIPO_LOGIN_KEY = '@portal/tlk'
  private readonly TOKEN_PARCEIRO_KEY = '@portal/tpk'
  private renegociacaoCartaoService: RenegociacaoCartaoService;
  private breadcrumbService: BreadcrumbService;

  constructor(
    private httpClient: HttpClient,
    private injector: Injector,
    private apiService: ApiService
  ) {
    this.identificarBandeira();
  }

  set bandeira(bandeira: Bandeira) {
    sessionStorage.setItem(this.KEY_BANDEIRA, bandeira);
  }

  get bandeira(): Bandeira {
    const bandeira = sessionStorage.getItem(this.KEY_BANDEIRA);
    return Bandeira[bandeira];
  }

  get autenticacao(): TokenAutenticacao | null {
    const token = sessionStorage.getItem(this.KEY_JWT_TOKEN_LOCAL_SOTAGE);
    if (token) {
      const [header, payload, SIGNATURE] = token.split('.');
      const autenticacao: TokenAutenticacao = JSON.parse(decodeURIComponent(escape(atob(payload))));
      return autenticacao;
    }

    return null;
  }

  get token(): string | null {
    return sessionStorage.getItem(this.KEY_JWT_TOKEN_LOCAL_SOTAGE);
  }

  get requestId() {
    const autenticacao = this.autenticacao;

    if (autenticacao) {
      return autenticacao.identificacaoLog;
    }

    return null;
  }

  get tokenParceiro(): string | null {
    const autenticacao = this.autenticacao;

    if (autenticacao) {
      return autenticacao.parceiro;
    }

    return null;
  }

  get colaboradorAutenticado(): Colaborador | null {
    const jsonColaborador = sessionStorage.getItem(this.KEY_CREDENCIAIS_COLABORADOR);
    if (jsonColaborador) {
      const colaboradorSalvo: Colaborador = JSON.parse(atob(jsonColaborador));
      const colaborador = new Colaborador();
      colaborador.empresa = colaboradorSalvo.empresa;
      colaborador.empresa = colaboradorSalvo.empresa;
      colaborador.filial = colaboradorSalvo.filial;
      colaborador.tafil = colaboradorSalvo.tafil;
      colaborador.matricula = colaboradorSalvo.matricula;
      colaborador.senha = colaboradorSalvo.senha;
      return colaborador;
    }
    return null;
  }

  set colaborador(colaborador: Colaborador) {
    sessionStorage.setItem(this.KEY_CREDENCIAIS_COLABORADOR, btoa(JSON.stringify(colaborador)));
  }

  private definirRotaRedirecionamentoLogin(contrato?: string, funcionalidade?: string) {
    if (!contrato || !funcionalidade) {
      return this.bandeira === Bandeira.CASAS_BAHIA ? '/portal' : '/portal/contratos';
    }

    if (funcionalidade === 'SEGUNDA_VIA') {
      return `portal/contratos/${contrato}/segundavia`;
    } else if (funcionalidade === 'RENEGOCIACAO') {
      return `portal/contratos/${contrato}/renegociacao`;
    } else if (funcionalidade === 'ACEITE_TERMO_RENEGOCIACAO') {
      return `portal/contratos/${contrato}/renegociacao/campanha/resumo`;
    }
  }

  logoutCliente(): void {
    sessionStorage.removeItem(this.KEY_JWT_TOKEN_LOCAL_SOTAGE);
    sessionStorage.removeItem("debitId");
    sessionStorage.removeItem("ofertaEscolhida");
    sessionStorage.removeItem("nrParcelaSelecionada");
    this.breadcrumbService.clearBreadcrumbs();
    this.renegociacaoCartaoService = this.injector.get(RenegociacaoCartaoService);
    this.renegociacaoCartaoService.limparDadosDivida();
  }

  logout(): void {
    sessionStorage.clear();
    this.renegociacaoCartaoService = this.injector.get(
      RenegociacaoCartaoService
    );
    this.renegociacaoCartaoService.limparDadosDivida();
    this.identificarBandeira();
  }

  armazenarInformacoesAutenticacao(token: string): void {
    sessionStorage.setItem(this.KEY_JWT_TOKEN_LOCAL_SOTAGE, token);
  }

  identificarBandeira(): void {
    this.bandeira = environment.bandeira as Bandeira;
  }

  temColaboradorAutenticado(): boolean {
    const autenticacao = this.autenticacao;
    return autenticacao && autenticacao.tipoAutenticacao === TipoAutenticacao.COLABORADOR;
  }

  async login(login: Usuario, tokenParceiro?: string): Promise<void> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/autenticacao/cliente',
      oldBrowserFallbackPath: '/cobrancas/autenticacao/cliente',
    });

    const autenticacaoCliente = new AutenticacaoCliente();
    autenticacaoCliente.bandeira = this.bandeira;
    autenticacaoCliente.cpf = login.cpf;
    autenticacaoCliente.senha = login.senha;
    autenticacaoCliente.identificacaoParceiro = tokenParceiro;

    const respostaAutenticacao = await this.httpClient.post<RespostaAutenticacao>(path, autenticacaoCliente).toPromise();

    if (tokenParceiro) {
      this.armazenaTokenParceiro(tokenParceiro);
    }

    this.armazenarTipoAutenticacao(TipoAutenticacao.CLIENTE);
    this.armazenarInformacoesAutenticacao(respostaAutenticacao.token);
  }

  async loginAutomaticoWithToken(redirectToken: string) {
    const [contrato, funcionalidade, token] = atob(redirectToken).split(':');
    this.armazenarInformacoesAutenticacao(token.trim());
    sessionStorage.setItem(this.KEY_IS_AUTO_LOGIN, JSON.stringify(true));
    return this.definirRotaRedirecionamentoLogin(contrato, funcionalidade);
  }

  async loginColaborador(colaborador: Colaborador, cliente?: Usuario): Promise<void> {
    const clientePreenchido = !!cliente;

    if (!clientePreenchido) {
      const path = this.apiService.getEndpoint({
        apiKey: 'cdc-cobranca-orquestrador-registro',
        path: '/cobranca-orquestrador-registro/cobrancas/autenticacao/colaborador',
        oldBrowserFallbackPath: '/cobrancas/autenticacao/colaborador',
      })

      const usuarioJ2 = colaborador.toUsuarioJ2();
      await this.httpClient.post<void>(path, usuarioJ2).toPromise();
      this.colaborador = colaborador;
    } else {
      const autenticacaoColaborador = new AutenticacaoColaborador();
      autenticacaoColaborador.bandeira = this.bandeira;
      autenticacaoColaborador.cpf = cliente.cpf;
      autenticacaoColaborador.dataNascimento = cliente.dataNascimento;
      autenticacaoColaborador.colaborador = colaborador.toUsuarioJ2();

      const path = this.apiService.getEndpoint({
        apiKey: 'cdc-cobranca-orquestrador-registro',
        path: '/cobranca-orquestrador-registro/cobrancas/autenticacao/colaborador/cliente',
        oldBrowserFallbackPath: '/cobrancas/autenticacao/colaborador/cliente',
      })

      const respostaAutenticacao = await this.httpClient.post<RespostaAutenticacao>(path, autenticacaoColaborador).toPromise();

      sessionStorage.setItem(this.KEY_POSSUI_CARNE, respostaAutenticacao?.possuiCarne.toString());

      this.armazenarTipoAutenticacao(TipoAutenticacao.COLABORADOR);
      this.armazenarInformacoesAutenticacao(respostaAutenticacao.token);
    }
  }

  async loginAutomatico(loginAutomatico: LoginAutomatico, authTicket: string, tokenParceiro?: string): Promise<string> {
    const autenticacaoAutomatica = new AutenticacaoAutomatica();
    autenticacaoAutomatica.bandeira = this.bandeira;
    autenticacaoAutomatica.cpf = loginAutomatico.cpf;
    autenticacaoAutomatica.dataNascimento = loginAutomatico.dataNascimento;
    autenticacaoAutomatica.identificacaoParceiro = tokenParceiro;
    autenticacaoAutomatica.authTicket = authTicket;

    const url = `${this.apiUrlService}/v1/cobranca-orquestrador-registro/cobrancas/autenticacao/automatico`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/autenticacao/automatico',
      oldBrowserFallbackPath: '/cobrancas/autenticacao/automatico',
    });

    const respostaAutenticacao = await this.httpClient.post<RespostaAutenticacao>(path, autenticacaoAutomatica).toPromise();

    if (authTicket) {
      this.armazenarTipoAutenticacao(TipoAutenticacao.AUTOMATICO_APP);
    } else {
      this.armazenarTipoAutenticacao(TipoAutenticacao.AUTOMATICO);
    }

    if (tokenParceiro) {
      this.armazenaTokenParceiro(tokenParceiro);
    }

    this.armazenarInformacoesAutenticacao(respostaAutenticacao.token);
    sessionStorage.setItem(this.KEY_IS_AUTO_LOGIN, JSON.stringify(true));
    sessionStorage.setItem(this.KEY_POSSUI_CARNE, respostaAutenticacao.possuiCarne.toString());

    return this.definirRotaRedirecionamentoLogin(loginAutomatico.contrato, loginAutomatico.funcionalidade);
  }

  async loginCampanha(idCliente: string, tokenParceiro?: string): Promise<string> {
    const url = `${this.apiUrlService}/v1/cobranca-orquestrador-registro/cobrancas/autenticacao/campanha`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/autenticacao/campanha',
      oldBrowserFallbackPath: '/cobrancas/autenticacao/campanha',
    })

    const autenticacaoCampanha = new AutenticacaoCampanha();
    autenticacaoCampanha.bandeira = this.bandeira;
    autenticacaoCampanha.idCliente = idCliente;
    autenticacaoCampanha.identificacaoParceiro = tokenParceiro;

    const respostaAutenticacao = await this.httpClient.post<RespostaAutenticacao>(path, autenticacaoCampanha).toPromise();
    this.armazenarTipoAutenticacao(TipoAutenticacao.CAMPANHA);

    if (tokenParceiro) {
      this.armazenaTokenParceiro(tokenParceiro);
    }

    this.armazenarInformacoesAutenticacao(respostaAutenticacao.token);

    sessionStorage.setItem(this.KEY_IS_AUTO_LOGIN, JSON.stringify(true));
    sessionStorage.setItem(this.KEY_POSSUI_CARNE, respostaAutenticacao.possuiCarne.toString());

    return this.definirRotaRedirecionamentoLogin(respostaAutenticacao.contrato, respostaAutenticacao.funcionalidade);
  }

  async consultaAutorizacaoCertificado() {
    const url = `${this.apiUrlService}/v1/cobranca-orquestrador-registro/cobrancas/status`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/status',
      oldBrowserFallbackPath: '/cobrancas/status',
    })
    return this.httpClient.get(path).toPromise();
  }

  get apiUrlService(): string {
    if (BrowserUtils.isOldFirefox()) {
      return environment.cdccorpApiURLService;
    }

    return environment.apiURLService;
  }

  async salvarDadosPreLogin(cliente: ClientePreLogin): Promise<PreLoginResponse> {
    const url = `${this.apiUrlService}/v1/cobranca/clientes/pre-login`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: '/cobranca/clientes/pre-login',
      oldBrowserFallbackPath: '/clientes/pre-login',
    });

    const response = await this.httpClient.post<PreLoginResponse>(path, cliente).toPromise();

    if (response) {
      sessionStorage.setItem(this.KEY_PRE_LOGIN_CLIENT_PHONE, cliente.telefone);
      sessionStorage.setItem(this.KEY_POSSUI_CARNE, response.possuiCarne.toString());
      this.armazenarHashRastreabilidade(response.id);
    }

    return response;
  }

  async salvarLogAcaoCliente(logAcaoCliente: LogAcaoCliente): Promise<void> {
    const dataToSend = { logAcaoCliente, origem: this.obterTipoAutenticacao() ?? TipoAutenticacao.CLIENTE };

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: '/cobranca/clientes/pre-login/log',
      oldBrowserFallbackPath: '/clientes/pre-login/log',
    })
    return this.httpClient.post<Promise<void>>(path, dataToSend).toPromise();
  }

  get preLoginClientPhone(): string | null {
    return sessionStorage.getItem(this.KEY_PRE_LOGIN_CLIENT_PHONE);
  }

  get isAutoLogin(): boolean {
    try {
      const isAutoLogin = Boolean(sessionStorage.getItem(this.KEY_IS_AUTO_LOGIN));
      return isAutoLogin;
    } catch (error) {
      return false;
    }
  }

  get possuiCarne(): boolean {
    try {
      const possuiCarne = sessionStorage.getItem(this.KEY_POSSUI_CARNE);
      return possuiCarne === 'true';
    } catch (error) {
      return false;
    }
  }

  armazenarHashRastreabilidade(hash: string) {
    sessionStorage.setItem(this.KEY_PRE_LOGIN_ID, hash);
  }

  obterHashRastreabilidade(): string {
    return sessionStorage.getItem(this.KEY_PRE_LOGIN_ID);
  }

  armazenarTipoAutenticacao(tipoAutenticacao: TipoAutenticacao) {
    sessionStorage.setItem(this.TIPO_LOGIN_KEY, tipoAutenticacao);
  }

  obterTipoAutenticacao(): TipoAutenticacao {
    return sessionStorage.getItem(this.TIPO_LOGIN_KEY) as TipoAutenticacao;
  }

  armazenaTokenParceiro(tokenParceiro: string) {
    sessionStorage.setItem(this.TOKEN_PARCEIRO_KEY, tokenParceiro);
  }

  obterTokenParceiro(): string {
    return sessionStorage.getItem(this.TOKEN_PARCEIRO_KEY);
  }

}
